import React, { useMemo, useState } from "react";
import __ from "src/utils/Translations";
import useIsAhr from "src/utils/hooks/useIsAhr";
import { useCompanyId, useCompanyName } from "src/Pages/Company/CompanyContext";
import { getCompanyBaseBreadcrumbs } from "src/Pages/Company/routerHelper";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import ImportExportLayout from "src/Pages/Company/OrganizationStructure/Layouts/ImportExportLayout";
import BackButton from "src/Pages/Company/OrganizationStructure/sharedComponents/BackButton";
import DownloadHistory, {
  ImportEntry,
} from "src/Pages/Company/OrganizationStructure/sharedComponents/DownloadHistory/DownloadHistory";
import { Button } from "@mui/material";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import FEATURE_FLAGS from "src/utils/constants/featureFlags";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import {
  organizationStructurePositionsExport,
  organizationStructurePositionsImport,
  organizationStructurePositionsImportHistory,
} from "src/utils/RoleBasedSecurity/permissions";
import { useFetchStructureData } from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/OrganizationStructure/api/useFetchStructureData";
import {
  handleFileDownload,
  ExportContentType,
} from "src/Pages/Company/OrganizationStructure/utils/handleFileDownload";
import {
  downloadPositionsData,
  importPositionData,
} from "src/Pages/Company/OrganizationStructure/api";
import useImportData from "src/Pages/Company/OrganizationStructure/api/useImportData";
import useImportModal from "src/Pages/Company/OrganizationStructure/sharedComponents/useImportModal";
import { getPositionsImportInstructions } from "./constants";

const ImportExportPositions = () => {
  const companyId = useCompanyId();
  const isAhr = useIsAhr();
  const companyName = useCompanyName();
  const { data: structureData } = useFetchStructureData(companyId);
  const structureId: string = structureData?.[0]?.structureId ?? "";

  const [processData, setProcessData] = useState<{ id: string } | undefined>(
    undefined
  );

  const { mutate: importData } = useImportData<{ id: string }>({
    mutationFn: (file: File) =>
      importPositionData({ companyId, structureId, file }),
    successMessage: __("Pomyślnie zaimportowano stanowiska"),
    errorMessage: __("Nie udało się zaimportować stanowisk"),
    onImportSuccess: (data) => setProcessData(data),
  });

  const { open: openImportModal } = useImportModal({
    title: __("Import danych stanowisk"),
    mutates: [importData],
  });

  const hasExportPositionsEnabled = useFeatureIsOn(
    FEATURE_FLAGS.ORGANIZATION_STRUCTURE_POSITIONS_EXPORT
  );
  const hasImportPositionsEnabled = useFeatureIsOn(
    FEATURE_FLAGS.ORGANIZATION_STRUCTURE_POSITIONS_IMPORT
  );
  const hasImportExportPositionsHistoryEnabled = useFeatureIsOn(
    FEATURE_FLAGS.ORGANIZATION_STRUCTURE_POSITIONS_IMPORT_EXPORT_HISTORY
  );
  const canExportPositions = usePermission(
    organizationStructurePositionsExport
  );
  const canImportPositions = usePermission(
    organizationStructurePositionsImport
  );
  const canPreviewImportHistory = usePermission(
    organizationStructurePositionsImportHistory
  );

  const baseBreadcrumbs = (
    getCompanyBaseBreadcrumbs(companyId, companyName) as {
      title: string;
      link: string;
    }[]
  ).map(({ title, link }) => ({
    label: title,
    to: link,
  }));

  const breadcrumbs = isAhr
    ? [
        {
          label: `${__("Zarządzanie firmą")} ${companyName}`,
          to: `/ahr`,
        },
        {
          label: __("Struktura organizacji"),
          to: `/ahr/organization-structure`,
        },
        {
          label: __("Stanowiska"),
          to: `/ahr/organization-structure/positions`,
        },
        {
          label: __("Import export stanowisk"),
          to: `/ahr/organization-structure/positions/import-export-positions`,
        },
      ]
    : [
        ...baseBreadcrumbs,
        {
          label: __("Struktura organizacji"),
          to: `/company/edit/${companyId}/organization-structure`,
        },
        {
          label: __("Stanowiska"),
          to: `/company/edit/${companyId}/organization-structure/positions`,
        },
        {
          label: __("Import export stanowisk"),
          to: `/company/edit/${companyId}/organization-structure/positions/import-export-positions`,
        },
      ];

  const imports = useMemo<ImportEntry[]>(
    () => (processData ? [{ processData }] : []),
    [processData]
  );

  const instructions = getPositionsImportInstructions();

  return (
    <>
      <BreadcrumbsMui items={breadcrumbs} />
      <BackButton sx={{ mt: 4, mb: 3, p: 0 }} />
      <ImportExportLayout>
        <ImportExportLayout.Header
          title={__("Eksport i import stanowisk")}
          description={__(
            "Na tej stronie możesz eksportować aktualną listę stanowisk do pliku Excel, a następnie dodać do niej nowe pozycje i zaktualizowaną listę zaimportować z powrotem do systemu."
          )}
        />
        {hasExportPositionsEnabled && canExportPositions && (
          <>
            <ImportExportLayout.SectionHeader title={__("Eksport stanowisk")} />
            <ImportExportLayout.ActionCard
              title={__("Eksportuj do pliku")}
              description={__("Eksportuj do pliku dane dotyczące stanowisk")}
              actions={
                <>
                  <Button
                    variant="outlined"
                    data-testid="os__positions-export-csv-button"
                    onClick={() =>
                      handleFileDownload(() =>
                        downloadPositionsData({
                          companyId,
                          structureId,
                          contentType: ExportContentType.CSV,
                        })
                      )
                    }
                  >
                    {__("Eksportuj plik CSV")}
                  </Button>
                  <Button
                    variant="outlined"
                    data-testid="os__positions-export-xlsx-button"
                    onClick={() =>
                      handleFileDownload(() =>
                        downloadPositionsData({
                          companyId,
                          structureId,
                          contentType: ExportContentType.XLSX,
                        })
                      )
                    }
                  >
                    {__("Eksportuj plik XLSX")}
                  </Button>
                </>
              }
            />
            <ImportExportLayout.Divider />
          </>
        )}
        <ImportExportLayout.SectionHeader title={__("Szablon do importów")} />
        <ImportExportLayout.ActionCard
          title={__("Pobierz szablon do importów")}
          description={__(
            "Pobrany szablon możesz wykorzystać do przygotowania importu stanowisk"
          )}
          actions={
            <>
              <Button
                variant="outlined"
                data-testid="os__positions-template-csv-button"
                onClick={() =>
                  handleFileDownload(() =>
                    downloadPositionsData({
                      companyId,
                      structureId,
                      contentType: ExportContentType.CSV,
                      template: true,
                    })
                  )
                }
              >
                {__("Pobierz szablon CSV")}
              </Button>
              <Button
                variant="outlined"
                data-testid="os__positions-template-xlsx-button"
                onClick={() =>
                  handleFileDownload(() =>
                    downloadPositionsData({
                      companyId,
                      structureId,
                      contentType: ExportContentType.XLSX,
                      template: true,
                    })
                  )
                }
              >
                {__("Pobierz szablon XLSX")}
              </Button>
            </>
          }
        />
        <ImportExportLayout.Divider />
        {hasImportPositionsEnabled && canImportPositions && (
          <>
            <ImportExportLayout.SectionHeader title={__("Import stanowisk")} />
            <ImportExportLayout.Instructions instructions={instructions} />
            <ImportExportLayout.ActionCard
              title={__("Importuj stanowiska")}
              description={__("Wgraj plik .csv lub .xlsx")}
              actions={
                <Button
                  variant="outlined"
                  data-testid="os__positions-import-button"
                  onClick={openImportModal}
                >
                  {__("Importuj")}
                </Button>
              }
            />
            {hasImportExportPositionsHistoryEnabled &&
              canPreviewImportHistory && <DownloadHistory imports={imports} />}
          </>
        )}
      </ImportExportLayout>
    </>
  );
};

export default ImportExportPositions;
